/* tslint:disable */
import { WeekOfYear } from './utils/time';
import { Duration, LocalDate, LocalDateTime } from 'js-joda';

import { Injectable, Provider, Type } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';


export enum ResponseType { EPHEMERAL = "EPHEMERAL", IN_CHANNEL = "IN_CHANNEL" }

export interface Attachment {
    author_icon: string | null;
    author_link: string | null;
    author_name: string | null;
    color: string | null;
    fallback: string | null;
    fields: Field[] | null;
    image_url: string | null;
    pretext: string | null;
    text: string | null;
    thumb_url: string | null;
    title: string | null;
    title_link: string | null;
}

export interface BillDetails {
    summary: BillSummary;
    tasks: EmployeeTaskInfo[];
}

export interface BillSummary {
    billId: number;
    billableTime: Duration;
    billing: number;
    createdAt: LocalDate;
    responsibleName: string;
}

export interface BillableWork {
    minutes: number | null;
    notes: string;
}

export interface CalendarEvent {
    description: string | null;
    end: LocalDateTime;
    start: LocalDateTime;
}

export interface ConferenceSheet {
    events: CalendarEvent[];
    weekOfYear: WeekOfYear;
}

export interface CreateBillRequest {
    lastIncludedDate: LocalDate | null;
    responsible: string | null;
}

export interface CreateCustomerProjectRequest {
    name: string;
}

export interface CreateVacationSourceDto {
    employees: string[];
    name: string;
    numberOfDays: number;
}

export interface CustomerProjectRole {
    assignees: string[];
    billingRate: number;
    id: number;
    title: string;
}

export interface CustomerProjectRoleDto {
    billingRate: number;
    employees: string[];
    name: string;
}

export interface EmployeeDetails {
    bitbucketLogin: string | null;
    cvId: string | null;
    email: string;
    firstName: string;
    ircNick: string | null;
    lastName: string;
    name: string;
    openIdIdentity: string | null;
    phone: string;
    postOffice: string | null;
    postalCode: string | null;
    skypeAccount: string | null;
    streetAddress: string | null;
}

export interface EmployeeInfo {
    active: boolean;
    bitbucketLogin: string | null;
    email: string | null;
    firstName: string | null;
    id: number;
    ircNick: string | null;
    lastName: string | null;
    login: string | null;
    name: string | null;
    phone: string | null;
    postOffice: string | null;
    postalCode: string | null;
    skypeAccount: string | null;
    streetAddress: string | null;
}

export interface EmployeeTaskInfo {
    billableTime: Duration;
    billing: number;
    billingRate: number;
    date: LocalDate;
    employeeId: number;
    employeeName: string;
    notes: string | null;
    roleId: number;
    roleTitle: string;
    workId: number;
}

export interface EmployeeUnbilledWorkSummary {
    billableTime: Duration;
    billing: number;
    employeeId: number;
    employeeName: string;
    maxDate: LocalDate;
    minDate: LocalDate;
    roleTitle: string;
}

export interface EmployeeVacationSourceSummary {
    name: string;
    numberOfDays: number;
}

export interface Field {
    short: boolean;
    title: string;
    value: string;
}

export interface Hoursheet {
    fridayDate: LocalDate;
    mondayDate: LocalDate;
    nextWeek: WeekOfYear;
    previousWeek: WeekOfYear;
    saturdayDate: LocalDate;
    sundayDate: LocalDate;
    tasks: TaskWeekHours[];
    thursdayDate: LocalDate;
    tuesdayDate: LocalDate;
    wednesdayDate: LocalDate;
    weekOfYear: WeekOfYear;
}

export interface LoginDetails {
    firstName: string;
    lastName: string;
    login: string;
    name: string;
}

export interface PasswordDetails {
    id: number;
    notes: string;
    password: string;
    title: string;
    username: string;
}

export interface PasswordForm {
    notes: string;
    password: string;
    title: string;
    username: string;
}

export interface PasswordInfo {
    id: number;
    notes: string;
    title: string;
    username: string;
}

export interface ProjectDetails {
    bills: BillSummary[];
    employeeUnbilledWorkSummaries: EmployeeUnbilledWorkSummary[];
    id: number;
    name: string;
    roles: CustomerProjectRole[];
    unbilledTasks: EmployeeTaskInfo[];
}

export interface ProjectId {
    id: number;
}

export interface ProjectSummary {
    billed: number;
    id: number;
    name: string;
    totalBilling: number;
    unbilled: number;
}

export interface SaveHoursRequest {
    date: LocalDate;
    minutes: number | null;
    notes: string | null;
    taskId: number;
}

export interface SaveHoursResponse {
    minutes: number | null;
}

export interface SlashCommandResponse {
    attachments: Attachment[] | null;
    response_type: ResponseType | null;
    text: string | null;
    username: string | null;
}

export interface TaskWeekHours {
    friday: BillableWork;
    id: number;
    monday: BillableWork;
    name: string;
    projectId: number;
    projectName: string;
    saturday: BillableWork;
    sunday: BillableWork;
    thursday: BillableWork;
    tuesday: BillableWork;
    wednesday: BillableWork;
}

export interface VacationCalendar {
    days: number[];
    month: number;
    year: number;
}

export interface VacationSourceSummary {
    assignees: string[];
    created: LocalDate;
    creator: string;
    name: string;
    numberOfDays: number;
}

export interface VacationSummary {
    employeeId: number;
    employeeLogin: string;
    employeeName: string;
    totalDays: number;
    unusedDays: number;
    usedDays: number;
}

declare const brand: unique symbol;

export type Branded<T, TBrand extends string> = T & { [brand]: TBrand };

abstract class ApinaConfigBase {

    /** Prefix added for all API calls */
    baseUrl: string = "";

    private readonly serializers: Record<string, Serializer> = {};

    protected constructor() {
        this.registerIdentitySerializer("any");
        this.registerIdentitySerializer("string");
        this.registerIdentitySerializer("number");
        this.registerIdentitySerializer("boolean");
    }

    serialize(value: unknown, type: string): any {
        return this.lookupSerializer(type).serialize(value);
    }

    deserialize(value: unknown, type: string): any {
        return this.lookupSerializer(type).deserialize(value);
    }

    registerSerializer(name: string, serializer: Serializer) {
        this.serializers[name] = serializer;
    }

    registerEnumSerializer(name: string, enumObject: any) {
        this.registerSerializer(name, nullSafeSerializer({
            serialize: o => enumObject[o],
            deserialize: o => enumObject[o],
        }));
    }

    registerClassSerializer<T = unknown>(name: string, fields: Record<keyof T, string>) {
        this.registerSerializer(name, nullSafeSerializer({
            serialize: o => Object.fromEntries(Object.entries(fields).map(([name, type]) => [name, this.serialize(o[name], type as string)])),
            deserialize: o => Object.fromEntries(Object.entries(fields).map(([name, type]) => [name, this.deserialize(o[name], type as string)])),
        }));
    }

    registerIdentitySerializer(name: string) {
        this.registerSerializer(name, identitySerializer);
    }

    registerDiscriminatedUnionSerializer<T = unknown>(name: string, discriminator: keyof T, types: Record<string, string>) {
        const self = this;
        this.registerSerializer(name, nullSafeSerializer({
            serialize(obj) {
                const localType = obj[discriminator];
                return {
                    ...self.lookupSerializer(types[localType]).serialize(obj),
                    [discriminator]: localType
                };
            },
            deserialize(obj) {
                const localType = obj[discriminator];
                return {
                    ...self.lookupSerializer(types[localType]).deserialize(obj),
                    [discriminator]: localType
                };
            }
        }));
    }

    private lookupSerializer(type: string): Serializer {
        if (!type) throw new Error("no type given");

        if (type.endsWith("[]")) {
            const elementType = type.substring(0, type.length - 2);
            return arraySerializer(this.lookupSerializer(elementType));
        }

        const dictionaryMatch = /^Record<string,\s*(.+)>$/.exec(type);
        if (dictionaryMatch)
            return dictionarySerializer(this.lookupSerializer(dictionaryMatch[1]));

        const serializer = this.serializers[type];
        if (serializer)
            return serializer;

        throw new Error(`could not find serializer for type '${type}'`);
    }
}

function arraySerializer(elementSerializer: Serializer): Serializer {
    return nullSafeSerializer({
        serialize: o => o.map((v: any) => elementSerializer.serialize(v)),
        deserialize: o => o.map((v: any) => elementSerializer.deserialize(v)),
    });
}

function dictionarySerializer(elementSerializer: Serializer): Serializer {
    return nullSafeSerializer({
        serialize: o => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, elementSerializer.serialize(v)])),
        deserialize: o => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, elementSerializer.deserialize(v)])),
    });
}

function formatQueryParameters(params: Record<string, unknown>): string {
    const components: string[] = [];

    const addQueryParameter = (encodedKey: string, value: any): void => {
        if (value != null)
            components.push(`${encodedKey}=${encodeURIComponent(value)}`);
    };

    for (const [key, value] of Object.entries(params || {})) {
        const encodedKey = encodeURIComponent(key);

        if (Array.isArray(value)) {
            for (const arrayItemValue of value)
                addQueryParameter(encodedKey, arrayItemValue);
        } else {
            addQueryParameter(encodedKey, value);
        }
    }

    return components.length > 0 ? '?' + components.join('&') : '';
}

export interface UrlData {
    uriTemplate: string;
    pathVariables?: any;
    requestParams?: any;
}

export interface RequestData extends UrlData {
    method: string;
    requestBody?: any;
    responseType?: string;
}

export interface Serializer<A = any, B = any> {
    serialize(o: A): B;

    deserialize(o: B): A;
}

const identitySerializer: Serializer = {
    serialize: o => o,
    deserialize: o => o
};

function nullSafeSerializer<A, B>(serializer: Serializer<A, B>): Serializer<A | null | undefined, B | null | undefined> {
    return {
        serialize: (o: A) => (o === null) ? null : (o === undefined) ? undefined : serializer.serialize(o),
        deserialize: (o: B) => (o === null) ? null : (o === undefined) ? undefined : serializer.deserialize(o),
    };
}

export abstract class ApinaEndpointContext {

    constructor(protected readonly config: ApinaConfig) {
    }

    abstract request(data: RequestData): Observable<any>

    url(data: UrlData): string {
        const url = this.buildUrl(data.uriTemplate, data.pathVariables);
        return url + formatQueryParameters(data.requestParams);
    }

    serialize(value: any, type: string): any {
        return this.config.serialize(value, type);
    }

    deserialize(value: any, type: string): any {
        return this.config.deserialize(value, type);
    }

    protected buildUrl(uriTemplate: String, pathVariables: any): string {
        return this.config.baseUrl + uriTemplate.replace(/{([^}]+)}/g, (_match, name) => pathVariables[name]);
    }
}

@Injectable()
export class DefaultApinaEndpointContext extends ApinaEndpointContext {

    constructor(private readonly httpClient: HttpClient, config: ApinaConfig) {
        super(config);
    }

    request(data: RequestData): Observable<any> {
        const url = this.buildUrl(data.uriTemplate, data.pathVariables);

        const requestParams = data.requestParams;
        let params: HttpParams | undefined = undefined;
        if (requestParams != null) {
            const filteredParams: { [key: string]: any }  = {};
            for (const key of Object.keys(requestParams)) {
                const value = requestParams[key];
                if (value != null)
                    filteredParams[key] = value;
            }

            params = new HttpParams({fromObject: filteredParams});
        }

        return this.httpClient.request(data.method, url, { params: params, body: data.requestBody })
            .pipe(map(r => data.responseType ? this.config.deserialize(r, data.responseType) : r));
    }
}

interface ProvideParams {
    config?: ApinaConfig;
    endpointContextClass?: Type<ApinaEndpointContext>;
}

export function provideApina(params: ProvideParams = {}): Provider[] {
    return [
        { provide: ApinaConfig, useValue: params.config ?? new ApinaConfig() },
        { provide: ApinaEndpointContext, useClass: params.endpointContextClass ?? DefaultApinaEndpointContext },
    ];
}

@Injectable({providedIn: 'root'})
export class ConferenceEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    getConferenceSheet(year: number, week: number): Observable<ConferenceSheet> {
        return this.context.request({
            'uriTemplate': '/api/conference/{year}/{week}',
            'method': 'GET',
            'pathVariables': {
                'year': this.context.serialize(year, 'number'),
                'week': this.context.serialize(week, 'number')
            },
            'responseType': 'ConferenceSheet'
        });
    }

}

@Injectable({providedIn: 'root'})
export class EmployeesEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    employeeDetails(login: string): Observable<EmployeeDetails> {
        return this.context.request({
            'uriTemplate': '/api/employee/{login}',
            'method': 'GET',
            'pathVariables': {
                'login': this.context.serialize(login, 'string')
            },
            'responseType': 'EmployeeDetails'
        });
    }

    employees(): Observable<EmployeeInfo[]> {
        return this.context.request({
            'uriTemplate': '/api/employee',
            'method': 'GET',
            'responseType': 'EmployeeInfo[]'
        });
    }

    updateEmployee(login: string, details: EmployeeDetails): Observable<EmployeeDetails> {
        return this.context.request({
            'uriTemplate': '/api/employee/{login}',
            'method': 'POST',
            'pathVariables': {
                'login': this.context.serialize(login, 'string')
            },
            'requestBody': this.context.serialize(details, 'EmployeeDetails'),
            'responseType': 'EmployeeDetails'
        });
    }

}

@Injectable({providedIn: 'root'})
export class FinnpilotEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    pilotwebDeployToProduction(): Observable<SlashCommandResponse> {
        return this.context.request({
            'uriTemplate': '/api/slack/finnpilot/pilotweb/deploy/production',
            'method': 'POST',
            'responseType': 'SlashCommandResponse'
        });
    }

    pilotwebPrepareRelease(): Observable<SlashCommandResponse> {
        return this.context.request({
            'uriTemplate': '/api/slack/finnpilot/pilotweb/prepare-release',
            'method': 'POST',
            'responseType': 'SlashCommandResponse'
        });
    }

    productionDbToQa(): Observable<SlashCommandResponse> {
        return this.context.request({
            'uriTemplate': '/api/slack/finnpilot/pilotweb/production-db-to-qa',
            'method': 'POST',
            'responseType': 'SlashCommandResponse'
        });
    }

}

@Injectable({providedIn: 'root'})
export class GitHubWebhookEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    webhook(payload: string): Observable<string> {
        return this.context.request({
            'uriTemplate': '/github/webhook',
            'method': 'POST',
            'requestBody': this.context.serialize(payload, 'string'),
            'responseType': 'string'
        });
    }

}

@Injectable({providedIn: 'root'})
export class GoatseEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    curse(): Observable<SlashCommandResponse> {
        return this.context.request({
            'uriTemplate': '/api/slack/goatse',
            'method': 'GET',
            'responseType': 'SlashCommandResponse'
        });
    }

}

@Injectable({providedIn: 'root'})
export class HaddockEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    curse(): Observable<SlashCommandResponse> {
        return this.context.request({
            'uriTemplate': '/api/slack/haddock',
            'method': 'GET',
            'responseType': 'SlashCommandResponse'
        });
    }

}

@Injectable({providedIn: 'root'})
export class HoursheetEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    hoursheet(user: string, year: number, week: number): Observable<Hoursheet> {
        return this.context.request({
            'uriTemplate': '/api/hoursheet/{user}/{year}/{week}',
            'method': 'GET',
            'pathVariables': {
                'user': this.context.serialize(user, 'string'),
                'year': this.context.serialize(year, 'number'),
                'week': this.context.serialize(week, 'number')
            },
            'responseType': 'Hoursheet'
        });
    }

    saveHours(user: string, request: SaveHoursRequest): Observable<SaveHoursResponse> {
        return this.context.request({
            'uriTemplate': '/api/hoursheet/{user}',
            'method': 'POST',
            'pathVariables': {
                'user': this.context.serialize(user, 'string')
            },
            'requestBody': this.context.serialize(request, 'SaveHoursRequest'),
            'responseType': 'SaveHoursResponse'
        });
    }

}

@Injectable({providedIn: 'root'})
export class KonttiEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    generateKontti(): Observable<SlashCommandResponse> {
        return this.context.request({
            'uriTemplate': '/api/slack/kontti',
            'method': 'GET',
            'responseType': 'SlashCommandResponse'
        });
    }

}

@Injectable({providedIn: 'root'})
export class LoginEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    loginDetails(): Observable<LoginDetails> {
        return this.context.request({
            'uriTemplate': '/api/login/details',
            'method': 'GET',
            'responseType': 'LoginDetails'
        });
    }

}

@Injectable({providedIn: 'root'})
export class ObliqueStrategiesEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    curse(): Observable<SlashCommandResponse> {
        return this.context.request({
            'uriTemplate': '/api/slack/oblique-strategies',
            'method': 'GET',
            'responseType': 'SlashCommandResponse'
        });
    }

}

@Injectable({providedIn: 'root'})
export class OodleEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    curse(): Observable<SlashCommandResponse> {
        return this.context.request({
            'uriTemplate': '/api/slack/oodle',
            'method': 'GET',
            'responseType': 'SlashCommandResponse'
        });
    }

}

@Injectable({providedIn: 'root'})
export class ProjectsEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    billDetails(projectId: number, id: number): Observable<BillDetails> {
        return this.context.request({
            'uriTemplate': '/api/projects/{projectId}/bills/{id}',
            'method': 'GET',
            'pathVariables': {
                'projectId': this.context.serialize(projectId, 'number'),
                'id': this.context.serialize(id, 'number')
            },
            'responseType': 'BillDetails'
        });
    }

    createBill(projectId: number, dto: CreateBillRequest): Observable<void> {
        return this.context.request({
            'uriTemplate': '/api/projects/{projectId}/bills',
            'method': 'POST',
            'pathVariables': {
                'projectId': this.context.serialize(projectId, 'number')
            },
            'requestBody': this.context.serialize(dto, 'CreateBillRequest')
        });
    }

    createProject(project: CreateCustomerProjectRequest): Observable<ProjectId> {
        return this.context.request({
            'uriTemplate': '/api/projects',
            'method': 'POST',
            'requestBody': this.context.serialize(project, 'CreateCustomerProjectRequest'),
            'responseType': 'ProjectId'
        });
    }

    createProjectRole(projectId: number, dto: CustomerProjectRoleDto): Observable<void> {
        return this.context.request({
            'uriTemplate': '/api/projects/{projectId}/roles',
            'method': 'POST',
            'pathVariables': {
                'projectId': this.context.serialize(projectId, 'number')
            },
            'requestBody': this.context.serialize(dto, 'CustomerProjectRoleDto')
        });
    }

    projectDetails(id: number): Observable<ProjectDetails> {
        return this.context.request({
            'uriTemplate': '/api/projects/{id}',
            'method': 'GET',
            'pathVariables': {
                'id': this.context.serialize(id, 'number')
            },
            'responseType': 'ProjectDetails'
        });
    }

    projectRole(projectId: number, roleId: number): Observable<CustomerProjectRoleDto> {
        return this.context.request({
            'uriTemplate': '/api/projects/{projectId}/roles/{roleId}',
            'method': 'GET',
            'pathVariables': {
                'projectId': this.context.serialize(projectId, 'number'),
                'roleId': this.context.serialize(roleId, 'number')
            },
            'responseType': 'CustomerProjectRoleDto'
        });
    }

    projects(): Observable<ProjectSummary[]> {
        return this.context.request({
            'uriTemplate': '/api/projects',
            'method': 'GET',
            'responseType': 'ProjectSummary[]'
        });
    }

    updateProjectRole(projectId: number, roleId: number, dto: CustomerProjectRoleDto): Observable<CustomerProjectRoleDto> {
        return this.context.request({
            'uriTemplate': '/api/projects/{projectId}/roles/{roleId}',
            'method': 'POST',
            'pathVariables': {
                'projectId': this.context.serialize(projectId, 'number'),
                'roleId': this.context.serialize(roleId, 'number')
            },
            'requestBody': this.context.serialize(dto, 'CustomerProjectRoleDto'),
            'responseType': 'CustomerProjectRoleDto'
        });
    }

}

@Injectable({providedIn: 'root'})
export class PublicHaddockEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    curse(target: string): Observable<string> {
        return this.context.request({
            'uriTemplate': '/api/public/haddock',
            'method': 'GET',
            'requestParams': {
                'target': this.context.serialize(target, 'string')
            },
            'responseType': 'string'
        });
    }

}

@Injectable({providedIn: 'root'})
export class RajaniemiEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    curse(): Observable<SlashCommandResponse> {
        return this.context.request({
            'uriTemplate': '/api/slack/rajaniemi',
            'method': 'GET',
            'responseType': 'SlashCommandResponse'
        });
    }

}

@Injectable({providedIn: 'root'})
export class Rot13Endpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    rot13(): Observable<SlashCommandResponse> {
        return this.context.request({
            'uriTemplate': '/api/slack/rot13',
            'method': 'GET',
            'responseType': 'SlashCommandResponse'
        });
    }

}

@Injectable({providedIn: 'root'})
export class SavedPasswordEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    addPassword(form: PasswordForm): Observable<void> {
        return this.context.request({
            'uriTemplate': '/api/saved-passwords',
            'method': 'POST',
            'requestBody': this.context.serialize(form, 'PasswordForm')
        });
    }

    deletePassword(id: number): Observable<void> {
        return this.context.request({
            'uriTemplate': '/api/saved-passwords/{id}',
            'method': 'DELETE',
            'pathVariables': {
                'id': this.context.serialize(id, 'number')
            }
        });
    }

    getPasswordDetails(id: number): Observable<PasswordDetails> {
        return this.context.request({
            'uriTemplate': '/api/saved-passwords/{id}',
            'method': 'GET',
            'pathVariables': {
                'id': this.context.serialize(id, 'number')
            },
            'responseType': 'PasswordDetails'
        });
    }

    listPasswords(): Observable<PasswordInfo[]> {
        return this.context.request({
            'uriTemplate': '/api/saved-passwords',
            'method': 'GET',
            'responseType': 'PasswordInfo[]'
        });
    }

    updatePasswordDetails(id: number, form: PasswordForm): Observable<void> {
        return this.context.request({
            'uriTemplate': '/api/saved-passwords/{id}',
            'method': 'PUT',
            'pathVariables': {
                'id': this.context.serialize(id, 'number')
            },
            'requestBody': this.context.serialize(form, 'PasswordForm')
        });
    }

}

@Injectable({providedIn: 'root'})
export class ShakespeareEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    curse(): Observable<SlashCommandResponse> {
        return this.context.request({
            'uriTemplate': '/api/slack/shakespeare',
            'method': 'GET',
            'responseType': 'SlashCommandResponse'
        });
    }

}

@Injectable({providedIn: 'root'})
export class TietokeskusEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    receiveRequest(): Observable<void> {
        return this.context.request({
            'uriTemplate': '/api/slack/tietokeskus',
            'method': 'GET'
        });
    }

}

@Injectable({providedIn: 'root'})
export class VCardExportEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    employeesVCard(): Observable<string> {
        return this.context.request({
            'uriTemplate': '/employees.vcf',
            'method': 'GET',
            'responseType': 'string'
        });
    }

}

@Injectable({providedIn: 'root'})
export class VacationsEndpoint {
    constructor(private readonly context: ApinaEndpointContext) {
    }

    calendar(login: string, year: number, month: number): Observable<VacationCalendar> {
        return this.context.request({
            'uriTemplate': '/api/vacations/{login}/calendar/{year}/{month}',
            'method': 'GET',
            'pathVariables': {
                'login': this.context.serialize(login, 'string'),
                'year': this.context.serialize(year, 'number'),
                'month': this.context.serialize(month, 'number')
            },
            'responseType': 'VacationCalendar'
        });
    }

    employeeSummary(login: string): Observable<VacationSummary> {
        return this.context.request({
            'uriTemplate': '/api/vacations/{login}/summary',
            'method': 'GET',
            'pathVariables': {
                'login': this.context.serialize(login, 'string')
            },
            'responseType': 'VacationSummary'
        });
    }

    employeeVacationSourceSummaries(login: string): Observable<EmployeeVacationSourceSummary[]> {
        return this.context.request({
            'uriTemplate': '/api/vacations/{login}/sources',
            'method': 'GET',
            'pathVariables': {
                'login': this.context.serialize(login, 'string')
            },
            'responseType': 'EmployeeVacationSourceSummary[]'
        });
    }

    saveVacationSource(dto: CreateVacationSourceDto): Observable<void> {
        return this.context.request({
            'uriTemplate': '/api/vacations/sources',
            'method': 'POST',
            'requestBody': this.context.serialize(dto, 'CreateVacationSourceDto')
        });
    }

    toggleVacation(login: string, year: number, month: number, day: number, state: string): Observable<VacationSummary> {
        return this.context.request({
            'uriTemplate': '/api/vacations/{login}/calendar/{year}/{month}/{day}',
            'method': 'POST',
            'pathVariables': {
                'login': this.context.serialize(login, 'string'),
                'year': this.context.serialize(year, 'number'),
                'month': this.context.serialize(month, 'number'),
                'day': this.context.serialize(day, 'number')
            },
            'requestBody': this.context.serialize(state, 'string'),
            'responseType': 'VacationSummary'
        });
    }

    vacationSourceSummaries(): Observable<VacationSourceSummary[]> {
        return this.context.request({
            'uriTemplate': '/api/vacations/sources',
            'method': 'GET',
            'responseType': 'VacationSourceSummary[]'
        });
    }

    vacationSummaries(): Observable<VacationSummary[]> {
        return this.context.request({
            'uriTemplate': '/api/vacations',
            'method': 'GET',
            'responseType': 'VacationSummary[]'
        });
    }

}

export class ApinaConfig extends ApinaConfigBase {
    constructor() {
        super();


        this.registerIdentitySerializer('ResponseType');

        this.registerClassSerializer<Attachment>('Attachment', {
            'author_icon': 'string',
            'author_link': 'string',
            'author_name': 'string',
            'color': 'string',
            'fallback': 'string',
            'fields': 'Field[]',
            'image_url': 'string',
            'pretext': 'string',
            'text': 'string',
            'thumb_url': 'string',
            'title': 'string',
            'title_link': 'string'
        });

        this.registerClassSerializer<BillDetails>('BillDetails', {
            'summary': 'BillSummary',
            'tasks': 'EmployeeTaskInfo[]'
        });

        this.registerClassSerializer<BillSummary>('BillSummary', {
            'billId': 'number',
            'billableTime': 'Duration',
            'billing': 'number',
            'createdAt': 'LocalDate',
            'responsibleName': 'string'
        });

        this.registerClassSerializer<BillableWork>('BillableWork', {
            'minutes': 'number',
            'notes': 'string'
        });

        this.registerClassSerializer<CalendarEvent>('CalendarEvent', {
            'description': 'string',
            'end': 'LocalDateTime',
            'start': 'LocalDateTime'
        });

        this.registerClassSerializer<ConferenceSheet>('ConferenceSheet', {
            'events': 'CalendarEvent[]',
            'weekOfYear': 'WeekOfYear'
        });

        this.registerClassSerializer<CreateBillRequest>('CreateBillRequest', {
            'lastIncludedDate': 'LocalDate',
            'responsible': 'string'
        });

        this.registerClassSerializer<CreateCustomerProjectRequest>('CreateCustomerProjectRequest', {
            'name': 'string'
        });

        this.registerClassSerializer<CreateVacationSourceDto>('CreateVacationSourceDto', {
            'employees': 'string[]',
            'name': 'string',
            'numberOfDays': 'number'
        });

        this.registerClassSerializer<CustomerProjectRole>('CustomerProjectRole', {
            'assignees': 'string[]',
            'billingRate': 'number',
            'id': 'number',
            'title': 'string'
        });

        this.registerClassSerializer<CustomerProjectRoleDto>('CustomerProjectRoleDto', {
            'billingRate': 'number',
            'employees': 'string[]',
            'name': 'string'
        });

        this.registerClassSerializer<EmployeeDetails>('EmployeeDetails', {
            'bitbucketLogin': 'string',
            'cvId': 'string',
            'email': 'string',
            'firstName': 'string',
            'ircNick': 'string',
            'lastName': 'string',
            'name': 'string',
            'openIdIdentity': 'string',
            'phone': 'string',
            'postOffice': 'string',
            'postalCode': 'string',
            'skypeAccount': 'string',
            'streetAddress': 'string'
        });

        this.registerClassSerializer<EmployeeInfo>('EmployeeInfo', {
            'active': 'boolean',
            'bitbucketLogin': 'string',
            'email': 'string',
            'firstName': 'string',
            'id': 'number',
            'ircNick': 'string',
            'lastName': 'string',
            'login': 'string',
            'name': 'string',
            'phone': 'string',
            'postOffice': 'string',
            'postalCode': 'string',
            'skypeAccount': 'string',
            'streetAddress': 'string'
        });

        this.registerClassSerializer<EmployeeTaskInfo>('EmployeeTaskInfo', {
            'billableTime': 'Duration',
            'billing': 'number',
            'billingRate': 'number',
            'date': 'LocalDate',
            'employeeId': 'number',
            'employeeName': 'string',
            'notes': 'string',
            'roleId': 'number',
            'roleTitle': 'string',
            'workId': 'number'
        });

        this.registerClassSerializer<EmployeeUnbilledWorkSummary>('EmployeeUnbilledWorkSummary', {
            'billableTime': 'Duration',
            'billing': 'number',
            'employeeId': 'number',
            'employeeName': 'string',
            'maxDate': 'LocalDate',
            'minDate': 'LocalDate',
            'roleTitle': 'string'
        });

        this.registerClassSerializer<EmployeeVacationSourceSummary>('EmployeeVacationSourceSummary', {
            'name': 'string',
            'numberOfDays': 'number'
        });

        this.registerClassSerializer<Field>('Field', {
            'short': 'boolean',
            'title': 'string',
            'value': 'string'
        });

        this.registerClassSerializer<Hoursheet>('Hoursheet', {
            'fridayDate': 'LocalDate',
            'mondayDate': 'LocalDate',
            'nextWeek': 'WeekOfYear',
            'previousWeek': 'WeekOfYear',
            'saturdayDate': 'LocalDate',
            'sundayDate': 'LocalDate',
            'tasks': 'TaskWeekHours[]',
            'thursdayDate': 'LocalDate',
            'tuesdayDate': 'LocalDate',
            'wednesdayDate': 'LocalDate',
            'weekOfYear': 'WeekOfYear'
        });

        this.registerClassSerializer<LoginDetails>('LoginDetails', {
            'firstName': 'string',
            'lastName': 'string',
            'login': 'string',
            'name': 'string'
        });

        this.registerClassSerializer<PasswordDetails>('PasswordDetails', {
            'id': 'number',
            'notes': 'string',
            'password': 'string',
            'title': 'string',
            'username': 'string'
        });

        this.registerClassSerializer<PasswordForm>('PasswordForm', {
            'notes': 'string',
            'password': 'string',
            'title': 'string',
            'username': 'string'
        });

        this.registerClassSerializer<PasswordInfo>('PasswordInfo', {
            'id': 'number',
            'notes': 'string',
            'title': 'string',
            'username': 'string'
        });

        this.registerClassSerializer<ProjectDetails>('ProjectDetails', {
            'bills': 'BillSummary[]',
            'employeeUnbilledWorkSummaries': 'EmployeeUnbilledWorkSummary[]',
            'id': 'number',
            'name': 'string',
            'roles': 'CustomerProjectRole[]',
            'unbilledTasks': 'EmployeeTaskInfo[]'
        });

        this.registerClassSerializer<ProjectId>('ProjectId', {
            'id': 'number'
        });

        this.registerClassSerializer<ProjectSummary>('ProjectSummary', {
            'billed': 'number',
            'id': 'number',
            'name': 'string',
            'totalBilling': 'number',
            'unbilled': 'number'
        });

        this.registerClassSerializer<SaveHoursRequest>('SaveHoursRequest', {
            'date': 'LocalDate',
            'minutes': 'number',
            'notes': 'string',
            'taskId': 'number'
        });

        this.registerClassSerializer<SaveHoursResponse>('SaveHoursResponse', {
            'minutes': 'number'
        });

        this.registerClassSerializer<SlashCommandResponse>('SlashCommandResponse', {
            'attachments': 'Attachment[]',
            'response_type': 'ResponseType',
            'text': 'string',
            'username': 'string'
        });

        this.registerClassSerializer<TaskWeekHours>('TaskWeekHours', {
            'friday': 'BillableWork',
            'id': 'number',
            'monday': 'BillableWork',
            'name': 'string',
            'projectId': 'number',
            'projectName': 'string',
            'saturday': 'BillableWork',
            'sunday': 'BillableWork',
            'thursday': 'BillableWork',
            'tuesday': 'BillableWork',
            'wednesday': 'BillableWork'
        });

        this.registerClassSerializer<VacationCalendar>('VacationCalendar', {
            'days': 'number[]',
            'month': 'number',
            'year': 'number'
        });

        this.registerClassSerializer<VacationSourceSummary>('VacationSourceSummary', {
            'assignees': 'string[]',
            'created': 'LocalDate',
            'creator': 'string',
            'name': 'string',
            'numberOfDays': 'number'
        });

        this.registerClassSerializer<VacationSummary>('VacationSummary', {
            'employeeId': 'number',
            'employeeLogin': 'string',
            'employeeName': 'string',
            'totalDays': 'number',
            'unusedDays': 'number',
            'usedDays': 'number'
        });

    }

}

